import api from "app/api";
import useExcelUrl from "app/useExcelUrl";
import { globalMessageHandler } from "common";
import useSettings from "features/settings/useSettings";
import moment from "moment";
import { createContext, useContext, useEffect, useState } from "react";

export type AdvancedReportData = {
  amount: number;
  boat: string;
  canceled_amount: number;
  admin_fee_amount: number;
  canceled_count: number;
  canceled_guests: number;
  refund_amount: number;
  refund_guests: number;
  count: number;
  email: string;
  guests: number;
  name: string;
  partner: string;
  partner_group: string;
  program: string;
  program_variant: string;
  t: string; // This represents a date, but in the example, it's a string
};

export type AdvancedReportDataFilterType = {
  from: string; //date string like "2024-01-01"
  to: string; //date string like "2024-01-01"
  boatIds?: number[];
  programIds?: number[];
  currency?: null | "EUR" | "HUF";
  partnerIds?: number[]; // Array of partner IDs
  sourceIds?: number[]; // Array of source IDs
};

export type AdvancedReportDataContextType = {
  data: AdvancedReportData[];
  filter: AdvancedReportDataFilterType;
  setFilter: (filter: AdvancedReportDataFilterType) => void;
  load: () => Promise<void>;
  loading: boolean;
  excelUrl?: string;
  excelWithBooking?: boolean;
  setExcelWithBooking: (excelWithBooking: boolean) => void;
};

export const AdvancedReportDataContext =
  createContext<AdvancedReportDataContextType | null>(null);

export default function useAdvancedReportData() {
  const [filter, setFilter] = useState<AdvancedReportDataFilterType>({
    from: moment().startOf("week").format("YYYY-MM-DD"),
    to: moment().endOf("week").format("YYYY-MM-DD"),
  });
  const [loading, setLoading] = useState(false);
  const [excelWithBooking, setExcelWithBooking] = useState(true);
  const [data, setData] = useState<AdvancedReportData[]>([]);
  const [settings] = useSettings({ key: "advancedReport" });

  async function load() {
    //api is an axios instance
    setLoading(true);
    const { success, data, error } = await api.get("reports/advanced", {
      params: {
        ...filter,
        currency: settings.currency,
        dateType: settings.dateType,
      },
    });
    setLoading(false);
    if (success) {
      setData(data);
    } else {
      globalMessageHandler.alert({
        severity: "error",
        message: typeof error === "string" ? error : JSON.stringify(error),
      });
    }
  }

  const excelUrl = useExcelUrl(
    "reports",
    null,
    {
      ...filter,
      currency: settings.currency,
      ...settings,
      bookings: excelWithBooking ? 1 : 0,
    },
    false
  );

  useEffect(() => {
    load();
  }, [
    filter.programIds,
    filter.boatIds,
    filter.from,
    filter.to,
    filter.partnerIds,
    filter.sourceIds,
    settings.currency,
    settings.dateType,
  ]);
  return {
    data,
    filter,
    setFilter,
    load,
    loading,
    excelUrl,
    excelWithBooking,
    setExcelWithBooking,
  };
}

export function useAdvancedReportDataContext() {
  const context = useContext(AdvancedReportDataContext);
  if (!context) {
    throw Error(
      "useAdvancedReportDataContext cannot be used outside of AdvancedReportDataProvider."
    );
  }
  return context;
}
